/* Copyright (c) 2021-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { When } from "react-if";
import { Link } from "react-router-dom";
import { Cell } from "react-table";
import { FlexContainer, SVGIcon, Text, theme, Tooltip } from "@nordcloud/gnui";
import { Provider } from "~/generated/graphql";
import { TableExpanderTemplate } from "~/components/Tables/Columns/Expander";
import { ProviderIcons } from "~/components/Tables/Columns/ProviderIcons";

export type AnomalyDetailVisual = {
  type: string;
  anomaly: boolean;
  cost: string;
  anomalyCost: string;
  anomalyCostRaw: string;
  provider?: Provider | null;
  minDailyCost: string;
  maxDailyCost: string;
  subRows?: AnomalyDetailVisual[];
  nid?: string;
  isLink?: boolean;
};

export const ANOMALIES_TABLE = [
  {
    Header: "Resource Type",
    accessor: "type",
    width: "60%",
    disableSortBy: true,
    Cell: ({ row }: Cell<AnomalyDetailVisual>) => {
      const iconComponent = row.original.provider ? (
        <ProviderIcons provider={row.original.provider} />
      ) : null;
      const nameComponent = row.original.isLink ? (
        <Link to={`/estate/${row.original.nid}`}>{row.original.type}</Link>
      ) : (
        <Text tag="span">{row.original.type}</Text>
      );
      return (
        <FlexContainer columnGap={theme.spacing.spacing02}>
          <TableExpanderTemplate raw row={row} />
          {iconComponent}
          {nameComponent}
        </FlexContainer>
      );
    },
  },
  {
    Header: () => (
      <FlexContainer justifyContent="flex-end">Min. Daily Cost</FlexContainer>
    ),
    accessor: "minDailyCost",
    width: "10%",
    disableSortBy: true,
    hasLeftBorder: true,
    Cell: ({ row }: Cell<AnomalyDetailVisual>) => (
      <FlexContainer justifyContent="flex-end">
        {row.original.minDailyCost}
      </FlexContainer>
    ),
  },
  {
    Header: () => (
      <FlexContainer justifyContent="flex-end">Max. Daily Cost</FlexContainer>
    ),
    accessor: "maxDailyCost",
    width: "10%",
    disableSortBy: true,
    hasLeftBorder: true,
    Cell: ({ row }: Cell<AnomalyDetailVisual>) => (
      <FlexContainer justifyContent="flex-end">
        {row.original.maxDailyCost}
      </FlexContainer>
    ),
  },
  {
    Header: () => (
      <FlexContainer justifyContent="flex-end">Daily Cost</FlexContainer>
    ),
    accessor: "dailyCost",
    width: "10%",
    disableSortBy: true,
    hasLeftBorder: true,
    Cell: ({ row }: Cell<AnomalyDetailVisual>) => (
      <FlexContainer justifyContent="flex-end">
        {row.original.cost}
      </FlexContainer>
    ),
  },
  {
    Header: () => (
      <FlexContainer justifyContent="flex-end">
        Anomaly Cost
        <SVGIcon
          name="chevronDown"
          css={{
            marginLeft: theme.spacing.spacing02,
            marginTop: `-${theme.spacing.spacing01}`,
            width: "0.75rem",
            height: "1rem",
          }}
        />
      </FlexContainer>
    ),
    accessor: "anomalyCost",
    width: "10%",
    disableSortBy: true,
    hasLeftBorder: true,
    Cell: ({ row }: Cell<AnomalyDetailVisual>) => (
      <FlexContainer justifyContent="flex-end">
        <When condition={row.original.anomaly}>~&nbsp;</When>
        <div css={{ position: "relative" }}>
          {row.original.anomaly ? (
            <Tooltip
              status="notification"
              caption={`Precise anomaly cost: ${row.original.anomalyCostRaw}`}
              minWidth="10rem"
              css={{ whiteSpace: "nowrap" }}
            >
              {row.original.anomalyCost}
            </Tooltip>
          ) : (
            <Text tag="span">{row.original.anomalyCost}</Text>
          )}
        </div>
      </FlexContainer>
    ),
  },
];
